import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Target, Mail, TrendingUp, Lightbulb, Copy, Check, ArrowLeft, Lock, BookOpen, Star, MessageSquare, Share2 } from 'lucide-react';
import SeoHead from '../components/SeoHead';
import api from '../utils/api';

const OUTREACH_TEMPLATES = [
  {
    title: 'Request Review from Reader',
    template: `Hi [Name],\n\nI hope you enjoyed [Book Title]! If you have a moment, I'd really appreciate an honest review on Amazon. Reviews are incredibly important for indie authors like me — even a few sentences help.\n\nHere's the direct link: [Amazon URL]\n\nThank you so much for your support!\n\n[Your Name]`,
  },
  {
    title: 'Book Blogger Outreach',
    template: `Hi [Blogger Name],\n\nI'm [Your Name], author of [Book Title] — a [genre] that [one-line hook].\n\nI've been following your reviews on [Blog/Channel Name] and love your take on [genre] books. Would you be interested in a complimentary copy for an honest review?\n\nThe book is available on Amazon: [URL]\n\nNo pressure at all — I just thought it might be a good fit for your audience.\n\nBest,\n[Your Name]`,
  },
  {
    title: 'ARC Reader Invitation',
    template: `Subject: Free Advance Copy — [Book Title] (Releasing [Date])\n\nHi [Name],\n\nI'd love to invite you to be an Advance Reader for my upcoming book, [Book Title].\n\nAs an ARC reader, you'll receive a free copy before the official launch in exchange for an honest Amazon review posted within [X] days of release.\n\nInterested? Reply with "I'm in!" and I'll send you the download link.\n\nThanks!\n[Your Name]`,
  },
  {
    title: 'Follow-Up After Purchase',
    template: `Hi [Name],\n\nThank you for purchasing [Book Title]! I hope you're enjoying it.\n\nQuick favor: if you've finished the book and have a moment, an Amazon review would mean the world to me. It helps other readers discover the book and supports indie authors like me.\n\n[Amazon Review Link]\n\nEven a one-line review makes a huge difference. Thank you!\n\n[Your Name]`,
  },
];

const AMAZON_TIPS = [
  { title: 'Optimize Your Book Title & Subtitle', desc: 'Include your primary keyword in the subtitle. Example: "The Morning Blueprint: A Busy Professional\'s Guide to Productive Mornings" — targets both the book name and the search term.' },
  { title: 'Choose the Right Categories', desc: 'Pick 2 specific, low-competition categories rather than broad ones. Use Publisher Rocket or manual research to find categories where you can realistically rank in the top 20.' },
  { title: 'Write a Keyword-Rich Description', desc: 'Your book description should read naturally but include 5-10 relevant search terms. Use HTML formatting (bold, bullets) to make it scannable. First 3 lines matter most — they show before "Read more".' },
  { title: 'Use All 7 Keyword Slots', desc: 'Amazon gives you 7 keyword fields (50 chars each). Don\'t repeat words from your title. Use long-tail phrases like "business book for new entrepreneurs" instead of single words.' },
  { title: 'Price Strategically for Launch', desc: 'Launch at $0.99-$2.99 for the first week to maximize downloads and reviews. Then raise to your target price ($4.99-$14.99). The initial volume signals Amazon\'s algorithm to promote your book.' },
  { title: 'Get 25+ Reviews in 30 Days', desc: 'Amazon\'s algorithm favors books with 25+ reviews. Use The Authors\' Review Exchange, your email list, and ARC readers to hit this threshold fast. This is the single biggest factor in organic discovery.' },
];

const SOCIAL_CHECKLIST = [
  'Create a "book reveal" post with your cover image',
  'Share a behind-the-scenes writing process story',
  'Post 3 quote graphics from your book',
  'Record a 60-second "why I wrote this book" video',
  'Share your Amazon link with a personal story, not just "buy my book"',
  'Engage in 5 book-related communities/groups per day',
  'Create an author website with email signup',
  'Send a launch email to your list with a direct Amazon link',
  'Ask 10 friends/family to post reviews on launch day',
  'Run a free chapter giveaway to build your email list',
];

export default function GrowthHub() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [copied, setCopied] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('club_token');
    setIsLoggedIn(!!token);
  }, []);

  const copyTemplate = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopied(id);
    setTimeout(() => setCopied(null), 2000);
  };

  if (!isLoggedIn) {
    return (
      <>
        <SeoHead title="Growth Hub — Book Promotion Resources | The Authors' Review Exchange" noindex={true} />
        <section className="min-h-screen flex items-center justify-center px-4 pt-20">
          <div className="text-center max-w-md">
            <Lock className="w-12 h-12 text-primary mx-auto mb-4" strokeWidth={1.5} />
            <h1 className="font-serif text-2xl mb-2">Members Only</h1>
            <p className="text-sm text-muted-foreground mb-6">The Growth Hub is available exclusively to Authors' Review Exchange members. Join free to access all resources.</p>
            <Link to="/club/dashboard" className="inline-flex items-center gap-2 rounded-sm px-6 py-3 bg-primary text-primary-foreground font-semibold text-sm" data-testid="growth-hub-join">
              Join Free — Get Access
            </Link>
          </div>
        </section>
      </>
    );
  }

  return (
    <>
      <SeoHead title="Growth Hub — Book Promotion Resources | The Authors' Review Exchange" noindex={true} />
      <section className="pt-28 pb-20 md:pt-36 md:pb-32">
        <div className="max-w-[1000px] mx-auto px-4 sm:px-6 md:px-12 lg:px-20">
          <Link to="/club/dashboard" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-primary mb-6" data-testid="back-to-club">
            <ArrowLeft className="w-3 h-3" /> Back to Dashboard
          </Link>

          <div className="mb-12">
            <p className="font-sans uppercase tracking-[0.2em] text-xs font-bold text-primary mb-3">Members Only</p>
            <h1 className="font-serif text-3xl md:text-4xl tracking-tight mb-3" data-testid="growth-hub-title">
              The <span className="text-primary">Growth Hub</span>
            </h1>
            <p className="text-muted-foreground text-sm max-w-lg">Templates, strategies, and checklists to promote your book, get more reviews, and climb the Amazon charts.</p>
          </div>

          {/* Outreach Templates */}
          <div className="mb-12">
            <h2 className="font-serif text-xl mb-6 flex items-center gap-2"><MessageSquare className="w-5 h-5 text-primary" /> Outreach Templates</h2>
            <div className="space-y-4">
              {OUTREACH_TEMPLATES.map((t, i) => (
                <div key={i} className="bg-white/5 border border-white/10 rounded-sm p-5" data-testid={`template-${i}`}>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-serif text-sm">{t.title}</h3>
                    <button onClick={() => copyTemplate(t.template, i)} className="flex items-center gap-1 text-xs text-primary hover:underline" data-testid={`copy-template-${i}`}>
                      {copied === i ? <><Check className="w-3 h-3" /> Copied</> : <><Copy className="w-3 h-3" /> Copy</>}
                    </button>
                  </div>
                  <pre className="text-xs text-muted-foreground whitespace-pre-wrap leading-relaxed font-sans bg-white/[0.02] p-4 rounded-sm border border-white/5">{t.template}</pre>
                </div>
              ))}
            </div>
          </div>

          {/* Amazon Optimization */}
          <div className="mb-12">
            <h2 className="font-serif text-xl mb-6 flex items-center gap-2"><TrendingUp className="w-5 h-5 text-primary" /> Amazon Optimization Tips</h2>
            <div className="space-y-3">
              {AMAZON_TIPS.map((tip, i) => (
                <div key={i} className="bg-white/5 border border-white/10 rounded-sm p-5" data-testid={`tip-${i}`}>
                  <h3 className="font-serif text-sm mb-1 text-primary">{i + 1}. {tip.title}</h3>
                  <p className="text-xs text-muted-foreground leading-relaxed">{tip.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Social Media Checklist */}
          <div className="mb-12">
            <h2 className="font-serif text-xl mb-6 flex items-center gap-2"><Share2 className="w-5 h-5 text-primary" /> Launch Day Checklist</h2>
            <div className="bg-white/5 border border-white/10 rounded-sm p-6">
              <div className="space-y-3">
                {SOCIAL_CHECKLIST.map((item, i) => (
                  <label key={i} className="flex items-start gap-3 cursor-pointer group" data-testid={`checklist-${i}`}>
                    <input type="checkbox" className="mt-1 accent-[#D4AF37] w-4 h-4" />
                    <span className="text-xs text-muted-foreground group-hover:text-foreground transition-colors">{item}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="bg-white/5 border border-primary/20 rounded-sm p-8 text-center">
            <h2 className="font-serif text-xl mb-3">Ready to Get More Reviews?</h2>
            <p className="text-sm text-muted-foreground mb-5">Use The Authors' Review Exchange alongside these strategies for maximum impact.</p>
            <Link to="/club/dashboard" className="inline-flex items-center gap-2 rounded-sm px-6 py-3 bg-primary text-primary-foreground font-semibold text-sm hover:bg-white hover:text-black transition-all">
              Go to Dashboard <ArrowLeft className="w-3.5 h-3.5 rotate-180" />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
